import { Grid, Typography, Box, Chip, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import React from "react";
import { api } from "../../../../api/api";
import { useAuth, userRoles } from "../../../../contexts/AuthProvider/AuthProvider";
import { formatDate } from "../../../../helpers";
import { StandadCard } from "../../../Cards";
import { Dialog } from "../../../Dialogs/Dialog";
import { StandardCellList } from "../../../Standards";
import { IPackageDetails } from "./IPackageDetails";
import { 
  LocalOffer, 
  DirectionsCar, 
  LocationOn, 
  AccessTime, 
  Build, 
  BatteryChargingFull,
  LocalGasStation,
  TireRepair,
  Emergency,
  LocalShipping,
  Security,
  CheckCircle,
  Cancel
} from "@mui/icons-material";

interface IVehicleList {
  vehicle_no: string;
  model_name: string;
  maker_name: string;
  status: "ACTIVE" | "PENDING";
  registration_year_v: number;
}

interface IPackageDetail {
  membership_code: string;
  package_name: string;
  duration: number;
  type: string;
  package_price: number;
  payment_date: string;
  created_by_name: string;
}

interface IPartnerTerms {
  partner_id: number;
  partner_name: string;
  partner_type: string;
  partner_terms: string[];
  status: string;
  insert_dt: string;
}

interface IPlanBenefit {
  Price: string;
  Towing: string;
  "Key Lockout": string;
  "Delivery Time": string;
  "Instant Repair": string;
  "Spares Delivery": string[];
  "Membership Transfer": string;
  "Battery Jump Start(Dead/Weak)": string;
  "Max. service allowed per month": string;
  "Fuel Delivery(Petrol or Diesel)": string;
  "Tyre Puncture(if stepney change)": string;
  "Emergency Roadside car inspection": string;
  "Tyre Puncture(if punctured repair)": string;
  "On-road Emergency Assistance Service": string[];
}

interface IPackageBenefits {
  package_id: number;
  old_package_id: number;
  package_order: number;
  package_name: string;
  package_price_excluding_gst: number;
  package_price: number;
  gst_percentage: number;
  gst_amount: number;
  vehicle_type: string;
  city: string;
  package_location_type: string;
  duration: number;
  package_type: string;
  additional_features: {
    "Vehicles Covered": string;
    "Locations Covered": string;
  };
  plan_benefits: IPlanBenefit[];
  card_url: string;
  partner_name: string;
  no_of_services: number;
  no_of_assistance: number | null;
  is_show: number;
  insert_dt: string;
  status: string;
  comment: string;
}

export const PackageDetails: React.FC<IPackageDetails> = (props) => {
  const { open, connection, onClose } = props;
  const [vehicleList, setVehicleList] = React.useState<IVehicleList[]>([]);
  const [packageDetails, setPackageDetails] = React.useState<IPackageDetail | null>(null);
  const [partnerTerms, setPartnerTerms] = React.useState<IPartnerTerms | null>(null);
  const [packageBenefits, setPackageBenefits] = React.useState<IPackageBenefits | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [partnerMessage, setPartnerMessage] = React.useState<string | null>(null);
  const [benefitsMessage, setBenefitsMessage] = React.useState<string | null>(null);
  const auth = useAuth();

  const {role}= auth.user;

  // const isVisible = role ==="CALL_CENTER_LEAD"
  const fetchPackageDetails = async () => {
    if (connection?.partner_id === 15) return;
    if (auth.user?.tokens) {
      try {
        const res = await api.get(
          `/check-vehicle-phone-exist-by-membershipcode?membership_code=${connection.membership_code}`,
          { headers: { "auth-Token": auth.user.tokens.access } }
        );
        if (res.data.getResult?.length) {
          setPackageDetails(res.data.getResult[0]);
          setMessage(null);
        } else {
          setPackageDetails(null);
          setMessage(res.data.message || "No record found");
        }
      } catch (err) {
        console.error(err);
      }
    }
  };

  const fetchPackageBenefits = async () => {
    if (connection?.partner_id === 15) return;
    if (auth.user?.tokens && connection.package_id) {
      try {
        const res = await api.get(
          `/get-package-by-id?package_id=${connection.package_id}`,
          { headers: { "auth-Token": auth.user.tokens.access } }
        );
        if (res.data.data?.length) {
          setPackageBenefits(res.data.data[0]);
          setBenefitsMessage(null);
        } else {
          setPackageBenefits(null);
          setBenefitsMessage(res.data.message || "No package benefits found");
        }
      } catch (err) {
        console.error(err);
        setBenefitsMessage("Error fetching package benefits");
      }
    }
  };

  const fetchPartnerTerms = async () => {
    if (connection?.partner_id === 15) return;
    if (auth.user?.role === "CALL_CENTER_LEAD" && connection.partner_id > 2) {
      try {
        const res = await api.get(
          `/check-partner-terms?partner_id=${connection.partner_id}`,
          // { headers: { "auth-Token": auth.user.tokens.access } }
        );
        if (res.data.getResult?.length) {
          setPartnerTerms(res.data.getResult[0]);
          setPartnerMessage(null);
        } else {
          setPartnerTerms(null);
          setPartnerMessage(res.data.message || "No partner terms found");
        }
      } catch (err) {
        console.error(err);
        setPartnerMessage("Error fetching partner terms");
      }
    }
  };

  React.useEffect(() => {
    if (connection?.partner_id === 15) {
      if (connection.package_id === 1) {
        setPackageDetails({
          membership_code: connection.membership_code || "",
          package_name: "Basic Plan",
          duration: 2,
          type: "Basic",
          package_price: 0,
          payment_date: connection.membership_start_date || "",
          created_by_name: "System",
        });
        setMessage(null);

        setPackageBenefits({
          package_id: 1,
          old_package_id: 1,
          package_order: 1,
          package_name: "Basic Plan",
          package_price_excluding_gst: 0,
          package_price: 0,
          gst_percentage: 0,
          gst_amount: 0,
          vehicle_type: "Two Wheeler",
          city: connection.city || "",
          package_location_type: "State",
          duration: 2,
          package_type: "Basic",
          additional_features: {
            "Vehicles Covered": "1",
            "Locations Covered": connection.state || "",
          },
          plan_benefits: [
            {
              "Accidental / Breakdown Towing": "INCLUDED",
              "Puncture Repair": "INCLUDED",
              "Battery Jumpstart Assistance": "INCLUDED",
              "On-spot Repairs": "INCLUDED",
              "Fuel Delivery": "INCLUDED",
              "24x7 Tele-assistance": "INCLUDED",
              "Route Guidance": "INCLUDED",
              "Ambulance Assistance (Tele)": "INCLUDED",
              "Online Police / Lawyer Assistance": "INCLUDED",
              "Key Lockout Assistance": "INCLUDED",
              "Cab Facility": "NOT INCLUDED",
              "Hotel Accommodation": "NOT INCLUDED",
            } as any,
          ],
          card_url: "",
          partner_name: "Partner 15",
          no_of_services: 3,
          no_of_assistance: null,
          is_show: 1,
          insert_dt: connection.membership_start_date || "",
          status: "ACTIVE",
          comment: "",
        });
        setBenefitsMessage(null);
      } else if (connection.package_id === 2) {
        setPackageDetails({
          membership_code: connection.membership_code || "",
          package_name: "Signature Plan",
          duration: 2,
          type: "Signature",
          package_price: 0,
          payment_date: connection.membership_start_date || "",
          created_by_name: "System",
        });
        setMessage(null);

        setPackageBenefits({
          package_id: 2,
          old_package_id: 2,
          package_order: 2,
          package_name: "Signature Plan",
          package_price_excluding_gst: 0,
          package_price: 0,
          gst_percentage: 0,
          gst_amount: 0,
          vehicle_type: "Two Wheeler",
          city: connection.city || "",
          package_location_type: "State",
          duration: 2,
          package_type: "Signature",
          additional_features: {
            "Vehicles Covered": "1",
            "Locations Covered": connection.state || "",
          },
          plan_benefits: [
            {
              "Accidental / Breakdown Towing": "INCLUDED",
              "Puncture Repair": "INCLUDED",
              "Battery Jumpstart Assistance": "INCLUDED",
              "On-spot Repairs": "INCLUDED",
              "Fuel Delivery": "INCLUDED",
              "24x7 Tele-assistance": "INCLUDED",
              "Cab Facility": "INCLUDED",
              "Ambulance Assistance (Tele)": "INCLUDED",
              "Online Police / Lawyer Assistance": "INCLUDED",
              "Route Guidance": "INCLUDED",
              "Key Lockout Assistance": "INCLUDED",
              "Hotel Accommodation": "NOT INCLUDED",
            } as any,
          ],
          card_url: "",
          partner_name: "Partner 15",
          no_of_services: 3,
          no_of_assistance: null,
          is_show: 1,
          insert_dt: connection.membership_start_date || "",
          status: "ACTIVE",
          comment: "",
        });
        setBenefitsMessage(null);
      } else if (connection.package_id === 3) {
        setPackageDetails({
          membership_code: connection.membership_code || "",
          package_name: "Elite Plan",
          duration: 2,
          type: "Elite",
          package_price: 0,
          payment_date: connection.membership_start_date || "",
          created_by_name: "System",
        });
        setMessage(null);

        setPackageBenefits({
          package_id: 3,
          old_package_id: 3,
          package_order: 3,
          package_name: "Elite Plan",
          package_price_excluding_gst: 0,
          package_price: 0,
          gst_percentage: 0,
          gst_amount: 0,
          vehicle_type: "Two Wheeler",
          city: connection.city || "",
          package_location_type: "State",
          duration: 2,
          package_type: "Elite",
          additional_features: {
            "Vehicles Covered": "1",
            "Locations Covered": connection.state || "",
          },
          plan_benefits: [
            {
              "Accidental / Breakdown Towing": "INCLUDED",
              "Puncture Repair": "INCLUDED",
              "Battery Jumpstart Assistance": "INCLUDED",
              "On-spot Repairs": "INCLUDED",
              "Fuel Delivery": "INCLUDED",
              "24x7 Tele-assistance": "INCLUDED",
              "Route Guidance": "INCLUDED",
              "Key Lockout Assistance": "INCLUDED",
              "Cab Facility": "INCLUDED",
              "Hotel Accommodation": "INCLUDED",
              "Ambulance Assistance (Tele)": "INCLUDED",
              "Online Police / Lawyer Assistance": "INCLUDED",
            } as any,
          ],
          card_url: "",
          partner_name: "Partner 15",
          no_of_services: 3,
          no_of_assistance: null,
          is_show: 1,
          insert_dt: connection.membership_start_date || "",
          status: "ACTIVE",
          comment: "",
        });
        setBenefitsMessage(null);
      } else {
        setPackageDetails(null);
        setMessage("No record found");
        setPackageBenefits(null);
        setBenefitsMessage("No package benefits found");
      }
      setPartnerTerms(null);
      setPartnerMessage("No partner terms found");
    } else {
      fetchPackageDetails();
      fetchPackageBenefits();
      if (connection.partner_id > 2) {
        fetchPartnerTerms();
      }
    }
  }, [connection]);

  const renderPlanBenefits = (benefits: IPlanBenefit) => {
    return (
      <TableContainer component={Paper} sx={{ boxShadow: 1, borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#4caf50' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>
                Feature
              </TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600, fontSize: '0.9rem' }}>
                {packageBenefits?.package_name || 'Package Details'}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(benefits).map(([key, value], index) => (
              <TableRow 
                key={key}
                sx={{ 
                  '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                  '&:hover': { backgroundColor: '#f0f0f0' }
                }}
              >
                <TableCell sx={{ 
                  borderRight: '1px solid #e0e0e0',
                  width: '50%',
                  fontWeight: 500,
                  fontSize: '0.85rem'
                }}>
                  {key}
                </TableCell>
                <TableCell sx={{ 
                  fontSize: '0.85rem',
                  color: '#333'
                }}>
                  {Array.isArray(value) ? (
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {value.map((item, itemIndex) => (
                        <Box
                          component="li"
                          key={itemIndex}
                          sx={{
                            mb: 0.5,
                            fontSize: '0.85rem',
                            color: '#333'
                          }}
                        >
                          {item}
                        </Box>
                      ))}
                    </Box>
                  ) : value === "INCLUDED" ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#2e7d32', gap: 0.5 }}>
                      <CheckCircle sx={{ fontSize: '1.1rem' }} />
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#2e7d32' }}>INCLUDED</Typography>
                    </Box>
                  ) : value === "NOT INCLUDED" ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#d32f2f', gap: 0.5 }}>
                      <Cancel sx={{ fontSize: '1.1rem' }} />
                      <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.8rem', color: '#d32f2f' }}>NOT INCLUDED</Typography>
                    </Box>
                  ) : (
                    value
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Dialog
      open={open}
      title="Customer Details"
      onClose={onClose}
      size="md"
      actions={[
        { type: "button", label: "Close", variant: "text", onClick: onClose },
      ]}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} lg={12}>
          <StandadCard heading="Package Details" sx={{ mb: 2 }}>
            {packageDetails ? (
              <StandardCellList
                colSize={3}
                list={[
                  {
                    heading: "Membership Code",
                    data: { type: "text", value: `# ${packageDetails?.membership_code}`|| "" },
                  },
                  {
                    heading: "Package Name",
                    data: { type: "text", value: packageDetails?.package_name || "" },
                  },
                  {
                    heading: "Amount Paid",
                    data: { type: "text", value: packageDetails?.package_price?.toString() || "" },
                  },
                  {
                    heading: "Duration",
                    data: { type: "text", value: `${packageDetails?.duration} Years` || ""},
                  },
                  {
                    heading: "Package Date",
                    data: { type: "text", value: formatDate(packageDetails?.payment_date) || ""},
                  },
                  {
                    heading: "Created By",
                    isHide:role=== userRoles.IT ? false : true,
                    data: { type: "text", value: packageDetails?.created_by_name || ""},
                  },
                ]}
              />
            ) : message ? (
              <p>{message}</p>
            ) : (
              <p>Loading package details...</p>
            )}
          </StandadCard>
        </Grid>

        {/* Package Benefits Section */}
        <Grid item xs={12} lg={12}>
          <StandadCard heading="Plan Benefits" sx={{ mb: 2 }}>
            {packageBenefits ? (
              <Box>
              
                {/* Plan Benefits */}
                {packageBenefits.plan_benefits && packageBenefits.plan_benefits.length > 0 && (
                  <Box>
                   
                    {renderPlanBenefits(packageBenefits.plan_benefits[0])}
                  </Box>
                )}
              </Box>
            ) : benefitsMessage ? (
              <Typography color="error">{benefitsMessage}</Typography>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <Typography>Loading plan benefits...</Typography>
              </Box>
            )}
          </StandadCard>
        </Grid>

        {connection.partner_id > 2 && auth.user?.role === "CALL_CENTER_LEAD" && (
          <Grid item xs={12} lg={12}>
            <StandadCard heading="Partner Terms" sx={{ mb: 2 }}>
                {partnerTerms ? (
                <StandardCellList
                  colSize={3}
                  list={[
                  {
                    heading: "Partner Name",
                    data: { type: "text", value: partnerTerms.partner_name },
                  },
                  {
                    heading: "Partner Type",
                    data: { type: "text", value: partnerTerms.partner_type },
                  },
                  ...partnerTerms.partner_terms.map((term, index) => ({
                    heading: `Term ${index + 1}`,
                    data: { type: "text" as "text", value: term },
                  })),
                  {
                    heading: "Status",
                    data: {
                    type: "text" as "text",
                    value: (
                      <span style={{ color: partnerTerms.status === "ACTIVE" ? "green" : "red" }}>
                      {partnerTerms.status}
                      </span>
                    ),
                    },
                  },
                  // {
                  //   heading: "Agreement Date",
                  //   data: { type: "text", value: formatDate(partnerTerms.insert_dt) },
                  // },
                  ]}
                />
                ) : partnerMessage ? (
                <p>{partnerMessage}</p>
                ) : (
                <p>Loading partner terms...</p>
                )}
            </StandadCard>
          </Grid>
        )}
      </Grid>
    </Dialog>
  );
};